import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import { TargetBlankMarker } from '#components/target-blank-marker.js';

import type { OrganizationDocumentStatusForUser } from '@cadstrom/backend/shared';

const TITLE_ID = 'document-compliance-blocked-title';
const DESCRIPTION_ID = 'document-compliance-blocked-description';

export interface DocumentComplianceBlockedScreenProps {
	/** Display name of the organization whose access is disabled. */
	organizationName: string;
	/** Documents awaiting an administrator's acceptance. */
	pendingDocuments: OrganizationDocumentStatusForUser['pendingDocuments'];
	/** Called when the user wants to switch orgs (e.g., via a menu item). */
	onRequestSwitchOrg?: () => void;
}

/**
 * Non-dismissable overlay for an organization blocked on documents the current
 * user may not accept — it names them and points at the administrator.
 *
 * @param props - See {@link DocumentComplianceBlockedScreenProps}.
 * @returns The blocked overlay.
 */
export function DocumentComplianceBlockedScreen(props: DocumentComplianceBlockedScreenProps) {
	const { organizationName, pendingDocuments, onRequestSwitchOrg } = props;

	return (
		<Dialog
			open
			disableEscapeKeyDown
			// Swallow close attempts: the user cannot resolve this themselves.
			onClose={() => undefined}
			maxWidth='sm'
			fullWidth
			aria-labelledby={TITLE_ID}
			aria-describedby={DESCRIPTION_ID}
			slotProps={{
				// MUI's default elevation paints pure-black shadows; the design system
				// uses a hairline over a tonal step.
				paper: { className: 'shadow-none!' },
				backdrop: { className: 'bg-gray-1/70 backdrop-blur-sm' },
			}}
		>
			<DialogTitle id={TITLE_ID} className='text-3xl font-semibold'>
				Access disabled — administrator acceptance required
			</DialogTitle>
			<DialogContent dividers>
				<p id={DESCRIPTION_ID} className='text-gray-12'>
					Access to Cadstrom for <strong>{organizationName}</strong> is disabled until an administrator of your
					organization accepts the documents below. Please contact your administrator.
				</p>

				{/* No bottom margin: the content padding is the spacing. */}
				<ul className='mt-8 flex list-none flex-col gap-2 p-0'>
					{pendingDocuments.map((doc) => (
						<li key={doc.versionId}>
							<a
								href={doc.url}
								target='_blank'
								rel='noopener noreferrer'
								className='text-blue-11 underline underline-offset-3 hover:text-blue-12'
							>
								{doc.name}
								<TargetBlankMarker />
							</a>
						</li>
					))}
				</ul>
			</DialogContent>
			{onRequestSwitchOrg === undefined ? null : (
				<DialogActions>
					<Button onClick={onRequestSwitchOrg}>
						<span>Switch organization</span>
					</Button>
				</DialogActions>
			)}
		</Dialog>
	);
}
